import { useCallback, useEffect, useRef, useState } from "react";

function usePlayer() {
    // state
    const [ isPlaying, setIsPlaying ] = useState(false);
    const [ duration, setDuration ] = useState(0);
    const [ currentTime, setCurrentTime ] = useState(0);

    // references
    const playerRef = useRef();        // reference player component
    const progressBarRef = useRef();   // reference progress bar
    const animationRef = useRef();     // reference animation

    // handle time jumps
    // useEffect(() => {
    //     timeTravel(timeJump);
    //     setIsPlaying(true);
    //     play();
    // }, [timeJump]);

    // YouTube options
    const options = {
        width: "100%",
        height: "100%",
        playerVars: {
            autoplay: 0, // 1 = on, 0 = off
            controls: 0, // 1 = on, 0 = off
        }
    }

    // load metadata once YouTube player is ready
    function readyPlayer(e) {
        let currentEvent = e.target;
        playerRef.current = currentEvent;
        
        // set track duration
        const seconds = Math.floor(playerRef.current.getDuration());
        setDuration(seconds);
        progressBarRef.current.max = seconds;
    }

    // keep YouTube play/pause button and custom play/pause button synced
    function handleStateChange(e) {
        let currentState = e.data;
        
        // show pause icon when track is playing or buffering
        if (currentState === 1 || currentState === 3) {
            setIsPlaying(true);
            animationRef.current = requestAnimationFrame(whilePlaying);
        } else {
            setIsPlaying(false);
            cancelAnimationFrame(animationRef.current);
        }
    }

    function calculateTime(secs) {
        const minutes = Math.floor(secs / 60);
        const returnedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
        const seconds = Math.floor(secs % 60);
        const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;

        return `${returnedMinutes}:${returnedSeconds}`;
    }

    const changePlayerCurrentTime = useCallback(() => {
        if (playerRef?.current.value !== "") {
            progressBarRef.current.style.setProperty('--progress-width', `${progressBarRef.current.value / duration * 100}%`);
            setCurrentTime(progressBarRef.current.value);
        }
    }, [duration]);

    const whilePlaying = useCallback(() => {
        if (playerRef?.current.value !== "") progressBarRef.current.value = playerRef?.current.getCurrentTime();

        changePlayerCurrentTime();
        animationRef.current = requestAnimationFrame(whilePlaying);
    }, [changePlayerCurrentTime]);

    const play = useCallback(() => {
        playerRef.current.playVideo();
        animationRef.current = requestAnimationFrame(whilePlaying);
    }, [whilePlaying]);

    function pause() {
        playerRef.current.pauseVideo();
        cancelAnimationFrame(animationRef.current);
    }

    function stop() {
        playerRef.current.stopVideo();

        // reset useRefs
        playerRef.current.value = "";
        progressBarRef.current.value = "";

        cancelAnimationFrame(animationRef.current);
    }

    const togglePlayPause = useCallback(() => {
        const prevValue = isPlaying;
        setIsPlaying(!prevValue);

        if (!prevValue) {
            play();
        } else {
            pause();
        }
    }, [isPlaying, play]);

    const changeRange = useCallback(() => {
        playerRef.current.seekTo(progressBarRef.current.value, true);
        changePlayerCurrentTime();
    }, [changePlayerCurrentTime]);

    function backTen() {
        timeTravel(Number(progressBarRef.current.value) - 10);
    }

    function forwardTen() {
        timeTravel(Number(progressBarRef.current.value) + 10);
    }

    const timeTravel = useCallback((newTime) => {
        progressBarRef.current.value = newTime;
        changeRange();
    }, [changeRange]);

    // finish playing
    useEffect(() => {
        if (Number(duration) > 1 && Number(currentTime) === Number(duration)) {
            togglePlayPause();
            timeTravel(0);
        }
    }, [currentTime, duration, timeTravel, togglePlayPause]);

    return {
        isPlaying,
        setIsPlaying,
        duration,
        setDuration,
        currentTime,
        playerRef,
        progressBarRef,
        options,
        readyPlayer,
        handleStateChange,
        calculateTime,
        play,
        togglePlayPause,
        stop,
        changeRange,
        backTen,
        forwardTen,
        timeTravel
    };
}

export { usePlayer }
